import React, { useState, useEffect, useMemo } from 'react'
import {
  Users,
  Building2,
  Settings,
  CheckCircle,
  XCircle,
  FileText,
  Calculator,
  ShoppingBag,
  Shield,
  UserPlus,
  Search,
  X,
  ChevronDown,
  Mail,
  Phone,
  Crown,
  User,
  Key,
  RefreshCw,
  Smartphone,
  AlertCircle,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu'
import {
  PermissionLevel,
  UserType,
  ExchangeUserPermission,
  FeaturePermissions,
  AccountUser,
} from '../types/exchange'
import { exchangeApi } from '../services/api/exchange'
import { roleLabel } from '../lib/roleFormatting'
import {
  DEFAULT_FEATURE_PERMISSIONS,
  FEATURE_LABELS,
} from '../lib/permissions'
import type { FeatureKey } from '../lib/permissions'
import { UserTypeSelector } from '../components/users/UserTypeSelector'
import type { ExtendedUserType } from '../components/users/UserTypeSelector'
import { UserPermissionsCard } from '../components/users/UserPermissionsCard'
import { UserPermissionsSummary } from '../components/users/UserPermissionsSummary'
import { useConfirm } from '../contexts/ConfirmProvider'

interface UserWithPermissions {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  accountRole?: 'owner' | 'admin' | 'member' | 'viewer'
  exchangePermissions?: ExchangeUserPermission[]
  allowedExchanges?: string[]
}

const PERMISSION_LABELS: Record<PermissionLevel, string> = {
  read: 'Read',
  write: 'Read & Write',
  read_only: 'Read Only',
}

const FEATURE_ICONS: Record<FeatureKey, React.ElementType> = {
  irs_forms: FileText,
  documents: FileText,
  forms: FileText,
  wizard: Building2,
  calculator: Calculator,
  marketplace: ShoppingBag,
  financials: Calculator,
}

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const { currentExchange, exchanges } = useExchange()
  const confirm = useConfirm()
  const [users, setUsers] = useState<UserWithPermissions[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddUser, setShowAddUser] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedExchange, setSelectedExchange] = useState<string | 'account' | null>(null)
  const [editingPermission, setEditingPermission] = useState<{
    userId: string
    exchangeId: string | 'account'
  } | null>(null)
  const [editingAccountUser, setEditingAccountUser] = useState<AccountUser | undefined>(undefined)
  const [managingUser, setManagingUser] = useState<{
    user: UserWithPermissions
    initialTab?: 'permissions' | 'password' | 'mfa' | 'settings'
  } | null>(null)

  useEffect(() => {
    const loadUsers = async () => {
      if (!selectedExchange) {
        setSelectedExchange('account')
        return
      }

      setIsLoading(true)
      try {
        if (selectedExchange === 'account') {
          const accounts = await exchangeApi.getAccounts()
          if (accounts.length > 0) {
            const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
            const enrichedUsers = await Promise.all(
              accountUsers.map(async (au) => {
                const userSearch = await exchangeApi.searchUsers('', accounts[0].id)
                const userData = userSearch.find(u => u.id === au.userId)
                return {
                  id: au.userId,
                  email: userData?.email || '',
                  firstName: userData?.firstName || '',
                  lastName: userData?.lastName || '',
                  phone: userData?.phone,
                  accountRole: au.role,
                  exchangePermissions: [],
                  allowedExchanges: au.allowedExchanges,
                }
              })
            )

            const validUsers = enrichedUsers.filter(u => u.firstName && u.lastName)

            const mockUsers: UserWithPermissions[] = [
              {
                id: 'mock_user_1',
                email: 'john.doe@open1031.ai',
                firstName: 'John',
                lastName: 'Doe',
                phone: '555-123-4567',
                accountRole: 'admin',
                exchangePermissions: [],
                allowedExchanges: [],
              },
              {
                id: 'mock_user_2',
                email: 'sarah.johnson@open1031.ai',
                firstName: 'Sarah',
                lastName: 'Johnson',
                phone: '555-987-6543',
                accountRole: 'member',
                exchangePermissions: [],
                allowedExchanges: [],
              },
            ]

            setUsers([...validUsers, ...mockUsers])
          }
        } else {
          const exchangeUsers = await exchangeApi.getExchangeUsers(selectedExchange)
          const enrichedUsers = exchangeUsers.map((eu: any) => ({
            id: eu.user?.id || eu.userId,
            email: eu.user?.email || '',
            firstName: eu.user?.firstName || '',
            lastName: eu.user?.lastName || '',
            accountRole: undefined,
            exchangePermissions: [eu],
          }))
          setUsers(enrichedUsers)
        }
      } catch (error) {
        console.error('Failed to load users:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUsers()
  }, [selectedExchange, currentExchange, currentUser])

  const allUsers = useMemo(() => {
    if (!currentUser) return users

    const currentUserInList = users.find(u => u.id === String(currentUser.id))
    if (currentUserInList) {
      return users.map(u =>
        u.id === String(currentUser.id)
          ? { ...u, accountRole: 'owner' as const }
          : u
      )
    }

    return [
      {
        id: String(currentUser.id),
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phone: currentUser.phone,
        accountRole: 'owner' as const,
        exchangePermissions: [],
      },
      ...users,
    ]
  }, [users, currentUser])

  const filteredUsers = useMemo(() => {
    let usersToFilter = allUsers

    if (currentUser?.email === 'newuser@open1031.ai') {
      usersToFilter = allUsers.filter(u => u.id === String(currentUser.id))
    }

    const filtered = usersToFilter.filter(u =>
      !searchQuery ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return filtered.sort((a, b) => {
      const aIsCurrent = a.id === String(currentUser?.id)
      const bIsCurrent = b.id === String(currentUser?.id)
      if (aIsCurrent && !bIsCurrent) return -1
      if (!aIsCurrent && bIsCurrent) return 1
      return 0
    })
  }, [allUsers, searchQuery, currentUser])

  const handleAddUser = async (
    email: string,
    exchangeId: string | 'account',
    userType: UserType,
    permissionLevel: PermissionLevel,
    featurePermissions?: FeaturePermissions,
    customTypeName?: string,
    allowedExchanges?: string[]
  ) => {
    try {
      if (exchangeId === 'account') {
        const accounts = await exchangeApi.getAccounts()
        if (accounts.length === 0) {
          throw new Error('No account found')
        }

        const searchResults = await exchangeApi.searchUsers(email)
        const existingUser = searchResults.find(u => u.email === email)

        if (existingUser) {
          await exchangeApi.addAccountUser(accounts[0].id, {
            userId: existingUser.id,
            role: permissionLevel === 'write' ? 'admin' : permissionLevel === 'read_only' ? 'viewer' : 'member',
            allowedExchanges: allowedExchanges && allowedExchanges.length > 0 ? allowedExchanges : undefined,
          })
        } else {
          throw new Error('User not found. Please invite them via exchange first.')
        }
      } else {
        await exchangeApi.addExchangeUser(exchangeId, {
          email,
          permissionLevel,
          userType,
          featurePermissions,
          customTypeName,
        })
      }

      setIsLoading(true)
      const accounts = await exchangeApi.getAccounts()
      if (accounts.length > 0 && exchangeId === 'account') {
        const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
        const enrichedUsers = await Promise.all(
          accountUsers.map(async (au) => {
            const userSearch = await exchangeApi.searchUsers('', accounts[0].id)
            const userData = userSearch.find(u => u.id === au.userId)
            return {
              id: au.userId,
              email: userData?.email || '',
              firstName: userData?.firstName || '',
              lastName: userData?.lastName || '',
              phone: userData?.phone,
              accountRole: au.role,
              exchangePermissions: [],
            }
          })
        )
        setUsers(enrichedUsers)
      } else if (exchangeId !== 'account') {
        const exchangeUsers = await exchangeApi.getExchangeUsers(exchangeId)
        const enrichedUsers = exchangeUsers.map((eu: any) => ({
          id: eu.user?.id || eu.userId,
          email: eu.user?.email || '',
          firstName: eu.user?.firstName || '',
          lastName: eu.user?.lastName || '',
          accountRole: undefined,
          exchangePermissions: [eu],
        }))
        setUsers(enrichedUsers)
      }
      setIsLoading(false)
      setShowAddUser(false)
    } catch (error: any) {
      console.error('Failed to add user:', error)
      alert(error.message || 'Failed to add user')
    }
  }

  const handleUpdatePermission = async (
    userId: string,
    exchangeId: string | 'account',
    updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }
  ) => {
    try {
      if (exchangeId === 'account') {
        const accounts = await exchangeApi.getAccounts()
        if (accounts.length > 0) {
          const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
          const accountUser = accountUsers.find(au => au.userId === userId)
          if (accountUser) {
            throw new Error('Account user updates with exchange selection not yet implemented in API')
          }
        }
      } else {
        await exchangeApi.updateExchangeUserPermission(exchangeId, userId, updates)

        const exchangeUsers = await exchangeApi.getExchangeUsers(exchangeId)
        const enrichedUsers = exchangeUsers.map((eu: any) => ({
          id: eu.user?.id || eu.userId,
          email: eu.user?.email || '',
          firstName: eu.user?.firstName || '',
          lastName: eu.user?.lastName || '',
          accountRole: undefined,
          exchangePermissions: [eu],
        }))
        setUsers(enrichedUsers)
      }
      setEditingPermission(null)
      setEditingAccountUser(undefined)
    } catch (error: any) {
      console.error('Failed to update permission:', error)
      alert(error.message || 'Failed to update permission')
    }
  }

  const handlePasswordReset = async (_userId: string) => {
    const ok = await confirm({
      title: 'Reset password?',
      description: 'They will receive an email with instructions to set a new password.',
      confirmText: 'Send reset email',
      icon: RefreshCw,
      iconColor: 'amber',
    })
    if (!ok) return
    try {
      alert('Password reset email sent successfully')
    } catch (error: any) {
      console.error('Failed to reset password:', error)
      alert(error.message || 'Failed to reset password')
    }
  }

  const handleMFAReset = async (_userId: string) => {
    const ok = await confirm({
      title: 'Reset MFA?',
      description: 'They will need to set up MFA again on their next login.',
      confirmText: 'Reset MFA',
      icon: RefreshCw,
      iconColor: 'amber',
    })
    if (!ok) return
    try {
      alert('MFA reset successfully. User will need to set up MFA again on next login.')
    } catch (error: any) {
      console.error('Failed to reset MFA:', error)
      alert(error.message || 'Failed to reset MFA')
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <Users className="h-7 w-7 text-primary" />
              User Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage account users and exchange-specific permissions
            </p>
          </div>
          <Button onClick={() => setShowAddUser(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      <div className="flex space-x-2 border-b border-border">
        <button
          onClick={() => setSelectedExchange('account')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            selectedExchange === 'account'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="h-4 w-4 inline mr-2" />
          Account Users
        </button>
        {currentExchange && (
          <button
            onClick={() => setSelectedExchange(currentExchange.id)}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              selectedExchange === currentExchange.id
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            {currentExchange.name} Permissions
          </button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <Card className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading users...</p>
          </Card>
        ) : filteredUsers.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No users found</p>
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <UserPermissionCard
              key={user.id}
              user={user}
              isCurrentUser={user.id === String(currentUser?.id)}
              selectedExchange={selectedExchange}
              currentExchangeId={currentExchange?.id}
              exchanges={exchanges}
              onEdit={async () => {
                const permission = { userId: user.id, exchangeId: selectedExchange || 'account' }
                setEditingPermission(permission)
                if (selectedExchange === 'account') {
                  const accounts = await exchangeApi.getAccounts()
                  if (accounts.length > 0) {
                    const accountUsers = await exchangeApi.getAccountUsers(accounts[0].id)
                    const accountUser = accountUsers.find(au => au.userId === user.id)
                    setEditingAccountUser(accountUser)
                  }
                } else {
                  setEditingAccountUser(undefined)
                }
              }}
              onUpdatePermission={handleUpdatePermission}
              onPasswordReset={() => handlePasswordReset(user.id)}
              onMFAReset={() => handleMFAReset(user.id)}
              onManage={(tab) => {
                setManagingUser({ user, initialTab: tab })
                if (selectedExchange === 'account') {
                  const accounts = exchangeApi.getAccounts()
                  accounts.then(async (accs) => {
                    if (accs.length > 0) {
                      const accountUsers = await exchangeApi.getAccountUsers(accs[0].id)
                      const accountUser = accountUsers.find(au => au.userId === user.id)
                      setEditingAccountUser(accountUser)
                    }
                  })
                } else {
                  setEditingPermission({ userId: user.id, exchangeId: selectedExchange || 'account' })
                }
              }}
            />
          ))
        )}
      </div>

      {showAddUser && (
        <AddUserModal
          exchanges={exchanges}
          currentExchange={currentExchange}
          onClose={() => setShowAddUser(false)}
          onAdd={handleAddUser}
        />
      )}

      {managingUser && (
        <UserManagementModal
          user={managingUser.user}
          initialTab={managingUser.initialTab || 'permissions'}
          selectedExchange={selectedExchange}
          exchanges={exchanges}
          currentExchange={currentExchange}
          currentPermission={selectedExchange !== 'account'
            ? managingUser.user.exchangePermissions?.find(p => p.exchangeId === selectedExchange)
            : undefined}
          accountUser={editingAccountUser}
          onClose={() => {
            setManagingUser(null)
            setEditingPermission(null)
            setEditingAccountUser(undefined)
          }}
          onSave={handleUpdatePermission}
          onPasswordReset={handlePasswordReset}
          onMFAReset={handleMFAReset}
        />
      )}

      {editingPermission && !managingUser && (
        <EditPermissionModal
          user={users.find(u => u.id === editingPermission.userId)!}
          exchangeId={editingPermission.exchangeId}
          exchange={editingPermission.exchangeId !== 'account' ? exchanges.find(e => e.id === editingPermission.exchangeId) : null}
          exchanges={exchanges}
          currentPermission={editingPermission.exchangeId !== 'account'
            ? users.find(u => u.id === editingPermission.userId)?.exchangePermissions?.find(p => p.exchangeId === editingPermission.exchangeId)
            : undefined}
          accountUser={editingAccountUser}
          onClose={() => {
            setEditingPermission(null)
            setEditingAccountUser(undefined)
          }}
          onSave={handleUpdatePermission}
        />
      )}
    </div>
  )
}

const UserPermissionCard: React.FC<{
  user: UserWithPermissions
  isCurrentUser?: boolean
  selectedExchange: string | 'account' | null
  currentExchangeId?: string
  exchanges: any[]
  onEdit: () => void
  onUpdatePermission: (userId: string, exchangeId: string | 'account', updates: Partial<ExchangeUserPermission>) => void
  onPasswordReset: (userId: string) => void
  onMFAReset: (userId: string) => void
  onManage?: (tab: 'permissions' | 'password' | 'mfa' | 'settings') => void
}> = ({ user, isCurrentUser = false, selectedExchange, exchanges, onEdit, onPasswordReset, onMFAReset, onManage }) => {
  const permission = selectedExchange === 'account'
    ? undefined
    : selectedExchange && user.exchangePermissions?.find(p => p.exchangeId === selectedExchange)

  const accountRole = user.accountRole || 'member'

  const userExchanges = user.allowedExchanges && user.allowedExchanges.length > 0
    ? exchanges.filter(ex => user.allowedExchanges?.includes(ex.id))
    : exchanges

  return (
    <Card className={`p-6 hover:shadow-lg transition-shadow ${isCurrentUser ? 'ring-2 ring-primary/20' : ''}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded-full bg-muted border-2 border-border flex items-center justify-center text-foreground font-bold text-lg relative flex-shrink-0 ${isCurrentUser ? 'ring-2 ring-primary/50' : ''}`}>
            {(user.firstName && user.lastName) ? `${user.firstName[0]}${user.lastName[0]}` : (user.email ? user.email[0].toUpperCase() : 'U')}
            {isCurrentUser && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <CheckCircle className="h-2.5 w-2.5 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                {user.firstName} {user.lastName}
              </h3>
              {isCurrentUser && (
                <Badge label="You" variant="success" icon={<User className="h-3 w-3" />} />
              )}
              {selectedExchange === 'account' ? (
                <Badge
                  label={`Account ${roleLabel(accountRole)}`}
                  variant={accountRole === 'owner' ? 'success' : accountRole === 'admin' ? 'info' : 'default'}
                  icon={accountRole === 'owner' ? <Crown className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                />
              ) : permission ? (
                <>
                  <Badge
                    label={roleLabel(permission.userType)}
                    variant="info"
                  />
                  <Badge
                    label={PERMISSION_LABELS[permission.permissionLevel]}
                    variant="default"
                  />
                </>
              ) : (
                <Badge label="No permissions set" variant="warning" icon={<AlertCircle className="h-3 w-3" />} />
              )}
              <span className="flex items-center text-sm text-muted-foreground ml-2">
                <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                {user.email}
              </span>
              {user.phone && (
                <span className="flex items-center text-sm text-muted-foreground">
                  <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                  {user.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManage ? onManage('permissions') : onEdit()}
            className="h-9 w-9 p-0"
            title="Manage Permissions"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManage ? onManage('password') : onPasswordReset(user.id)}
            className="h-9 w-9 p-0"
            title="Reset Password"
          >
            <Key className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManage ? onManage('mfa') : onMFAReset(user.id)}
            className="h-9 w-9 p-0"
            title="Reset MFA"
          >
            <Smartphone className="h-4 w-4" />
          </Button>
          {isCurrentUser && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onManage ? onManage('settings') : undefined}
              className="h-9 w-9 p-0"
              title="Refresh Session"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {selectedExchange === 'account' && userExchanges.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground mr-2">Exchange Access:</span>
            {user.allowedExchanges && user.allowedExchanges.length > 0 ? (
              userExchanges.map((ex) => (
                <Badge
                  key={ex.id}
                  label={ex.name}
                  variant="info"
                  icon={<Building2 className="h-3 w-3" />}
                />
              ))
            ) : (
              <Badge
                label="All Exchanges"
                variant="success"
                icon={<Building2 className="h-3 w-3" />}
              />
            )}
          </div>
        </div>
      )}

      {permission && permission.featurePermissions && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(permission.featurePermissions).map(([feature, enabled]) => {
              const Icon = FEATURE_ICONS[feature as FeatureKey]
              return (
                <div
                  key={feature}
                  className={`flex items-center gap-2 p-2 rounded-lg border ${
                    enabled
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : 'bg-muted text-muted-foreground border-border'
                  }`}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  <span className="text-xs font-medium flex-1">
                    {FEATURE_LABELS[feature as FeatureKey]}
                  </span>
                  {enabled ? (
                    <CheckCircle className="h-3 w-3 text-primary" />
                  ) : (
                    <XCircle className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Card>
  )
}

const Badge: React.FC<{
  label: string
  variant: 'success' | 'default' | 'info' | 'warning'
  icon?: React.ReactNode
}> = ({ label, variant, icon }) => {
  const variants = {
    success: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800',
    default: 'bg-muted text-muted-foreground border-border',
    info: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800',
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${variants[variant]}`}>
      {icon}
      {label}
    </span>
  )
}

const AddUserModal: React.FC<{
  exchanges: any[]
  currentExchange: any
  onClose: () => void
  onAdd: (
    email: string,
    exchangeId: string | 'account',
    userType: UserType,
    permissionLevel: PermissionLevel,
    featurePermissions?: FeaturePermissions,
    customTypeName?: string,
    allowedExchanges?: string[]
  ) => void
}> = ({ exchanges, currentExchange, onClose, onAdd }) => {
  const [email, setEmail] = useState('')
  const [scope, setScope] = useState<'account' | 'exchange'>('account')
  const [selectedExchangeId, setSelectedExchangeId] = useState(currentExchange?.id || '')
  const [userType, setUserType] = useState<ExtendedUserType>('exchanger')
  const permissionLevel: PermissionLevel = 'read'
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>({})
  const [customTypeName, setCustomTypeName] = useState('')
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>([])
  const [identityVerified, setIdentityVerified] = useState(false)
  const [kycComplete, setKycComplete] = useState(false)
  const [accreditedInvestor, setAccreditedInvestor] = useState(false)

  const showExchangerFields = userType === 'exchanger'

  const handleUserTypeChange = (type: ExtendedUserType) => {
    setUserType(type)
    if (type !== 'exchanger') {
      setIdentityVerified(false)
      setKycComplete(false)
      setAccreditedInvestor(false)
    }
    if (type !== 'custom' && type !== 'exchanger') {
      const presetFp = DEFAULT_FEATURE_PERMISSIONS[type as UserType]
      setFeaturePermissions(presetFp || {})
    }
  }

  const toggleExchange = (exchangeId: string) => {
    setSelectedExchanges(prev =>
      prev.includes(exchangeId)
        ? prev.filter(id => id !== exchangeId)
        : [...prev, exchangeId]
    )
  }

  const selectAllExchanges = () => {
    setSelectedExchanges(exchanges.map(e => e.id))
  }

  const deselectAllExchanges = () => {
    setSelectedExchanges([])
  }

  const handleAdd = () => {
    const resolvedUserType: UserType = userType === 'custom' ? 'viewer' : userType as UserType
    onAdd(
      email,
      scope === 'account' ? 'account' : selectedExchangeId,
      resolvedUserType,
      permissionLevel,
      scope === 'exchange' ? featurePermissions : undefined,
      userType === 'custom' ? customTypeName : undefined,
      scope === 'account' ? selectedExchanges : undefined
    )
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <UserPlus className="h-6 w-6 text-primary" />
              Add User
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-xl transition-colors">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <Input
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Scope
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setScope('account')}
                aria-pressed={scope === 'account'}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  scope === 'account'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/40'
                }`}
              >
                <Users className={`h-6 w-6 mx-auto mb-2 ${scope === 'account' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className={`font-medium ${scope === 'account' ? 'text-primary' : 'text-foreground'}`}>Account Level</div>
                <div className="text-xs text-muted-foreground mt-1">Select exchanges</div>
              </button>
              <button
                type="button"
                onClick={() => setScope('exchange')}
                aria-pressed={scope === 'exchange'}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  scope === 'exchange'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/40'
                }`}
              >
                <Building2 className={`h-6 w-6 mx-auto mb-2 ${scope === 'exchange' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className={`font-medium ${scope === 'exchange' ? 'text-primary' : 'text-foreground'}`}>Exchange Level</div>
                <div className="text-xs text-muted-foreground mt-1">Specific exchange</div>
              </button>
            </div>
          </div>

          {scope === 'exchange' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Exchange
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    {exchanges.find(e => e.id === selectedExchangeId)?.name || 'Select exchange'}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-full">
                  {exchanges.map((exchange) => (
                    <DropdownMenuItem
                      key={exchange.id}
                      onClick={() => setSelectedExchangeId(exchange.id)}
                    >
                      {exchange.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {scope === 'account' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Exchanges
              </label>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-muted-foreground">
                  {selectedExchanges.length === 0
                    ? 'No exchanges selected (user will have access to all exchanges)'
                    : `${selectedExchanges.length} of ${exchanges.length} exchanges selected`}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllExchanges} className="text-xs">
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAllExchanges} className="text-xs">
                    Clear All
                  </Button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-xl bg-card p-3 space-y-2">
                {exchanges.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">No exchanges available</div>
                ) : (
                  exchanges.map((exchange) => {
                    const isSelected = selectedExchanges.includes(exchange.id)
                    return (
                      <button
                        key={exchange.id}
                        type="button"
                        onClick={() => toggleExchange(exchange.id)}
                        aria-pressed={isSelected}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div className="text-left">
                            <div className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                              {exchange.name}
                            </div>
                            {exchange.address && (
                              <div className="text-xs text-muted-foreground mt-0.5">{exchange.address}</div>
                            )}
                          </div>
                        </div>
                        {isSelected ? (
                          <CheckCircle className="h-5 w-5 text-primary" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-border" />
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              User Type
            </label>
            <UserTypeSelector
              value={userType}
              onChange={handleUserTypeChange}
            />
            {userType === 'custom' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Custom Type Name
                </label>
                <Input
                  placeholder="e.g., Project Manager, Consultant, etc."
                  value={customTypeName}
                  onChange={(e) => setCustomTypeName(e.target.value)}
                />
              </div>
            )}
          </div>

          {userType === 'custom' ? (
            <UserPermissionsCard
              userType={userType}
              featurePermissions={featurePermissions}
              onChange={setFeaturePermissions}
              editable
            />
          ) : (
            <UserPermissionsSummary
              userType={userType}
              onCustomize={() => {
                setUserType('custom')
                const presetFp = DEFAULT_FEATURE_PERMISSIONS[userType as import('../types/exchange').UserType]
                setFeaturePermissions(presetFp || {})
              }}
            />
          )}

          {showExchangerFields && (
            <div className="space-y-2 pt-2 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Exchanger Verification Status
              </p>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={identityVerified}
                  onChange={(e) => setIdentityVerified(e.target.checked)}
                  className="rounded border-input"
                />
                Identity Verified
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={kycComplete}
                  onChange={(e) => setKycComplete(e.target.checked)}
                  className="rounded border-input"
                />
                KYC Complete
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={accreditedInvestor}
                  onChange={(e) => setAccreditedInvestor(e.target.checked)}
                  className="rounded border-input"
                />
                Accredited Investor
              </label>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex-shrink-0">
          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleAdd}
              className="flex-1"
              disabled={!email || (scope === 'exchange' && !selectedExchangeId)}
            >
              Add User
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const UserManagementModal: React.FC<{
  user: UserWithPermissions
  initialTab: 'permissions' | 'password' | 'mfa' | 'settings'
  selectedExchange: string | 'account' | null
  exchanges: any[]
  currentExchange: any
  currentPermission?: ExchangeUserPermission
  accountUser?: AccountUser
  onClose: () => void
  onSave: (
    userId: string,
    exchangeId: string | 'account',
    updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }
  ) => void
  onPasswordReset: (userId: string) => void
  onMFAReset: (userId: string) => void
}> = ({
  user,
  initialTab,
  selectedExchange,
  exchanges,
  currentExchange,
  currentPermission,
  accountUser,
  onClose,
  onSave,
  onPasswordReset,
  onMFAReset,
}) => {
  const [activeTab, setActiveTab] = useState<'permissions' | 'password' | 'mfa' | 'settings'>(initialTab)
  const [passwordResetConfirmed, setPasswordResetConfirmed] = useState(false)
  const [mfaResetConfirmed, setMfaResetConfirmed] = useState(false)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
                <Settings className="h-6 w-6 text-primary" />
                Manage User: {user.firstName} {user.lastName}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {user.email} • {selectedExchange === 'account' ? 'Account Level' : currentExchange?.name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-xl transition-colors">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="border-b border-border px-6">
          <div className="flex gap-1">
            {(['permissions', 'password', 'mfa', 'settings'] as const).map((tab) => {
              const tabConfig = {
                permissions: { label: 'Permissions', Icon: Shield },
                password: { label: 'Password', Icon: Key },
                mfa: { label: 'MFA', Icon: Smartphone },
                settings: { label: 'Settings', Icon: Settings },
              }[tab]
              const { label, Icon } = tabConfig
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-medium text-sm transition-colors relative ${
                    activeTab === tab
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {label}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'permissions' && (
            <PermissionForm
              user={user}
              exchangeId={selectedExchange || 'account'}
              exchange={selectedExchange !== 'account' ? currentExchange : null}
              exchanges={exchanges}
              currentPermission={currentPermission}
              accountUser={accountUser}
              onSave={onSave}
              onClose={onClose}
            />
          )}

          {activeTab === 'password' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1">Reset Password</h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      This will send a password reset email to <strong>{user.email}</strong>. The user will receive instructions to set a new password.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={passwordResetConfirmed}
                    onChange={(e) => setPasswordResetConfirmed(e.target.checked)}
                    className="w-4 h-4 rounded border-input"
                  />
                  <span className="text-sm text-foreground">I confirm I want to reset the password for this user</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (passwordResetConfirmed) {
                      onPasswordReset(user.id)
                      alert('Password reset email sent successfully')
                      onClose()
                    }
                  }}
                  disabled={!passwordResetConfirmed}
                >
                  Send Reset Email
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'mfa' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-1">Reset MFA</h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      This will reset the multi-factor authentication for <strong>{user.firstName} {user.lastName}</strong>. They will need to set up MFA again on their next login.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mfaResetConfirmed}
                    onChange={(e) => setMfaResetConfirmed(e.target.checked)}
                    className="w-4 h-4 rounded border-input"
                  />
                  <span className="text-sm text-foreground">I confirm I want to reset MFA for this user</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (mfaResetConfirmed) {
                      onMFAReset(user.id)
                      alert('MFA reset successfully. User will need to set up MFA again on next login.')
                      onClose()
                    }
                  }}
                  disabled={!mfaResetConfirmed}
                >
                  Reset MFA
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <RefreshCw className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Session Management</h3>
                    <p className="text-sm text-muted-foreground">
                      Refresh the user's session token. This will require them to log in again on their next request.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    alert('Session refreshed successfully')
                    onClose()
                  }}
                >
                  Refresh Session
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const PermissionForm: React.FC<{
  user: UserWithPermissions
  exchangeId: string | 'account'
  exchange: any
  exchanges: any[]
  currentPermission?: ExchangeUserPermission
  accountUser?: AccountUser
  onSave: (
    userId: string,
    exchangeId: string | 'account',
    updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }
  ) => void
  onClose: () => void
}> = ({
  user,
  exchangeId,
  exchange: _exchange,
  exchanges,
  currentPermission,
  accountUser,
  onSave,
  onClose,
}) => {
  const initialType: ExtendedUserType = currentPermission?.userType
    ? currentPermission.userType as ExtendedUserType
    : 'exchanger'

  const [userType, setUserType] = useState<ExtendedUserType>(initialType)
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>(
    currentPermission?.featurePermissions || {}
  )
  const [customTypeName, setCustomTypeName] = useState(
    (currentPermission as (typeof currentPermission & { customTypeName?: string }))?.customTypeName || ''
  )
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(
    accountUser?.allowedExchanges || []
  )
  const [identityVerified, setIdentityVerified] = useState(false)
  const [kycComplete, setKycComplete] = useState(false)
  const [accreditedInvestor, setAccreditedInvestor] = useState(false)

  const showExchangerFields = userType === 'exchanger'

  const handleUserTypeChange = (type: ExtendedUserType) => {
    setUserType(type)
    if (type !== 'exchanger') {
      setIdentityVerified(false)
      setKycComplete(false)
      setAccreditedInvestor(false)
    }
  }

  const toggleExchange = (exId: string) => {
    setSelectedExchanges(prev =>
      prev.includes(exId)
        ? prev.filter(id => id !== exId)
        : [...prev, exId]
    )
  }

  const selectAllExchanges = () => {
    setSelectedExchanges(exchanges.map(e => e.id))
  }

  const deselectAllExchanges = () => {
    setSelectedExchanges([])
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          User Type
        </label>
        <UserTypeSelector
          value={userType}
          onChange={handleUserTypeChange}
        />
        {userType === 'custom' && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-foreground mb-2">
              Custom Type Name
            </label>
            <Input
              placeholder="e.g., Project Manager, Consultant, etc."
              value={customTypeName}
              onChange={(e) => setCustomTypeName(e.target.value)}
            />
          </div>
        )}
      </div>

      {userType === 'custom' ? (
        <UserPermissionsCard
          userType={userType}
          featurePermissions={featurePermissions}
          onChange={setFeaturePermissions}
          editable
        />
      ) : (
        <UserPermissionsSummary
          userType={userType}
          onCustomize={() => {
            const presetFp = DEFAULT_FEATURE_PERMISSIONS[userType as import('../types/exchange').UserType]
            setFeaturePermissions(presetFp || {})
            handleUserTypeChange('custom')
          }}
        />
      )}

      {showExchangerFields && (
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Exchanger Verification Status
          </p>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={identityVerified}
              onChange={(e) => setIdentityVerified(e.target.checked)}
              className="rounded border-input"
            />
            Identity Verified
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={kycComplete}
              onChange={(e) => setKycComplete(e.target.checked)}
              className="rounded border-input"
            />
            KYC Complete
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={accreditedInvestor}
              onChange={(e) => setAccreditedInvestor(e.target.checked)}
              className="rounded border-input"
            />
            Accredited Investor
          </label>
        </div>
      )}

      {exchangeId === 'account' && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Select Exchanges
          </label>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-muted-foreground">
              {selectedExchanges.length === 0
                ? 'No exchanges selected (user will have access to all exchanges)'
                : `${selectedExchanges.length} of ${exchanges.length} exchanges selected`}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllExchanges} className="text-xs">
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAllExchanges} className="text-xs">
                Clear All
              </Button>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto border border-border rounded-xl bg-card p-3 space-y-2">
            {exchanges.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">No exchanges available</div>
            ) : (
              exchanges.map((ex) => {
                const isSelected = selectedExchanges.includes(ex.id)
                return (
                  <button
                    key={ex.id}
                    type="button"
                    onClick={() => toggleExchange(ex.id)}
                    aria-pressed={isSelected}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border bg-card hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="text-left">
                        <div className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                          {ex.name}
                        </div>
                        {ex.address && (
                          <div className="text-xs text-muted-foreground mt-0.5">{ex.address}</div>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <CheckCircle className="h-5 w-5 text-primary" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-border" />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}

      <div className="flex space-x-3 pt-4 border-t border-border">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={() => {
            const resolvedUserType: UserType = userType === 'custom' ? 'viewer' : userType as UserType
            onSave(user.id, exchangeId, {
              userType: resolvedUserType,
              featurePermissions: exchangeId !== 'account' ? featurePermissions : undefined,
              allowedExchanges: exchangeId === 'account' ? selectedExchanges : undefined,
            })
            onClose()
          }}
          className="flex-1"
        >
          Save Changes
        </Button>
      </div>
    </div>
  )
}

const EditPermissionModal: React.FC<{
  user: UserWithPermissions
  exchangeId: string | 'account'
  exchange: any
  exchanges: any[]
  currentPermission?: ExchangeUserPermission
  accountUser?: AccountUser
  onClose: () => void
  onSave: (
    userId: string,
    exchangeId: string | 'account',
    updates: Partial<ExchangeUserPermission> & { allowedExchanges?: string[] }
  ) => void
}> = ({ user, exchangeId, exchange, exchanges, currentPermission, accountUser, onClose, onSave }) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
                <Settings className="h-6 w-6 text-primary" />
                Manage Permissions
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {user.firstName} {user.lastName} • {exchangeId === 'account' ? 'Account Level' : exchange?.name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-xl transition-colors">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <PermissionForm
            user={user}
            exchangeId={exchangeId}
            exchange={exchange}
            exchanges={exchanges}
            currentPermission={currentPermission}
            accountUser={accountUser}
            onSave={onSave}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  )
}
